import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Modal from '../components/common/Modal';

const COMPANY_LOGO = 'https://z-cdn-media.chatglm.cn/files/be44215b-bdcf-430f-83f7-28f7b9010f52.jpeg?auth_key=1881343548-17790ad482434e639971872003b2f4b2-0-61e6631d0797f4a8820c340f2bc68587';

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '', rememberMe: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotMsg('success');
    } catch (err) {
      setForgotMsg(err.response?.data?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <>
      <style>{`
        /* LOGIN PAGE STYLES */
        .login-page {
          display: flex;
          min-height: 100vh;
          background: #f8fafc;
        }

        /* LEFT BRAND PANEL */
        .login-brand-panel {
          position: relative;
          width: 45%;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1e40af 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .brand-content {
          position: relative;
          z-index: 2;
          padding: 60px 48px;
          max-width: 480px;
        }

        .brand-circle {
          position: absolute;
          border-radius: 50%;
          background: rgba(59, 130, 246, 0.1);
        }

        .brand-circle-1 {
          width: 400px;
          height: 400px;
          top: -100px;
          right: -100px;
          animation: loginFloat 20s ease-in-out infinite;
        }

        .brand-circle-2 {
          width: 250px;
          height: 250px;
          bottom: -50px;
          left: -50px;
          animation: loginFloat 15s ease-in-out infinite reverse;
        }

        .brand-circle-3 {
          width: 150px;
          height: 150px;
          top: 40%;
          right: 10%;
          background: rgba(59, 130, 246, 0.15);
          animation: loginFloat 25s ease-in-out infinite;
        }

        @keyframes loginFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(10px, -10px) scale(1.02); }
          50% { transform: translate(-5px, 15px) scale(0.98); }
          75% { transform: translate(-15px, -5px) scale(1.01); }
        }

        .brand-logo {
          margin-bottom: 48px;
        }

        .logo-link {
          display: inline-block;
          text-decoration: none;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        .logo-link:hover {
          transform: scale(1.05);
          opacity: 0.9;
        }

        .logo-image-wrapper {
          /* Reduced from 150px to 110px */
          width: 110px;
          height: 110px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          padding: 12px;
          transition: background 0.3s ease;
        }

        .logo-link:hover .logo-image-wrapper {
          background: rgba(255, 255, 255, 0.12);
        }

        .logo-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 10px;
        }

        .brand-name {
          font-size: 32px;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 8px 0;
          letter-spacing: -0.025em;
        }

        .brand-tagline {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: 2px;
          font-weight: 500;
        }

        .brand-features {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        .brand-feature {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .feature-icon {
          width: 44px;
          height: 44px;
          min-width: 44px;
          background: rgba(59, 130, 246, 0.2);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #60a5fa;
        }

        .brand-feature h3 {
          color: #ffffff;
          font-size: 15px;
          font-weight: 600;
          margin: 0 0 4px 0;
        }

        .brand-feature p {
          color: rgba(255, 255, 255, 0.5);
          font-size: 13px;
          margin: 0;
          line-height: 1.5;
        }

        .brand-footer {
          margin-top: 48px;
          padding-top: 32px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .brand-footer p {
          color: rgba(255, 255, 255, 0.4);
          font-size: 13px;
          font-style: italic;
          line-height: 1.6;
          margin: 0;
        }

        /* RIGHT FORM PANEL */
        .login-form-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 40px;
          background: #ffffff;
        }

        .form-container {
          width: 100%;
          max-width: 420px;
        }

        .mobile-logo {
          display: none;
          align-items: center;
          gap: 12px;
          margin-bottom: 32px;
        }

        .mobile-logo-link {
          display: inline-flex;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }

        .mobile-logo-link:hover {
          opacity: 0.8;
        }

        .mobile-logo-img {
          /* Reduced from 56px to 40px */
          width: 40px;
          height: 40px;
          object-fit: contain;
          border-radius: 8px;
        }

        .mobile-logo span {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
        }

        .form-header {
          margin-bottom: 32px;
        }

        .form-header h2 {
          font-size: 28px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px 0;
          letter-spacing: -0.025em;
        }

        .form-header p {
          font-size: 15px;
          color: #64748b;
          margin: 0;
        }

        /* ALERT */
        .login-alert {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          border-radius: 10px;
          margin-bottom: 24px;
          font-size: 14px;
          animation: loginSlideDown 0.3s ease-out;
        }

        @keyframes loginSlideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .login-alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
        }

        .login-alert-icon {
          flex-shrink: 0;
          margin-top: 1px;
          color: #dc2626;
        }

        .login-alert-close {
          flex-shrink: 0;
          margin-left: auto;
          background: none;
          border: none;
          color: #dc2626;
          padding: 2px;
          opacity: 0.6;
          cursor: pointer;
          transition: opacity 0.2s;
        }

        .login-alert-close:hover {
          opacity: 1;
        }

        /* FORM */
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .login-form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .login-form-group label {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .form-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .form-label-row label {
          margin-bottom: 0;
        }

        .login-text-link {
          font-size: 13px;
          color: #2563eb;
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 500;
          padding: 0;
          transition: color 0.2s;
          font-family: inherit;
        }

        .login-text-link:hover {
          color: #1d4ed8;
          text-decoration: underline;
        }

        /* INPUT */
        .login-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .login-input-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          pointer-events: none;
          display: flex;
          align-items: center;
        }

        .login-input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          font-size: 14px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          outline: none;
          background: #f8fafc;
          color: #0f172a;
          transition: all 0.2s ease;
          font-family: inherit;
          box-sizing: border-box;
        }

        .login-input::placeholder {
          color: #94a3b8;
        }

        .login-input:hover {
          border-color: #cbd5e1;
          background: #ffffff;
        }

        .login-input:focus {
          border-color: #2563eb;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .login-toggle-password {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          transition: color 0.2s;
        }

        .login-toggle-password:hover {
          color: #64748b;
        }

        /* CHECKBOX */
        .login-checkbox-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }

        .login-checkbox-wrapper input[type="checkbox"] {
          display: none;
        }

        .login-checkmark {
          width: 18px;
          height: 18px;
          min-width: 18px;
          border: 1.5px solid #d1d5db;
          border-radius: 4px;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .login-checkbox-wrapper input[type="checkbox"]:checked + .login-checkmark {
          background: #2563eb;
          border-color: #2563eb;
        }

        .login-checkbox-wrapper input[type="checkbox"]:checked + .login-checkmark::after {
          content: '';
          width: 5px;
          height: 9px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
          margin-top: -2px;
        }

        .login-checkbox-label {
          font-size: 13px;
          color: #64748b;
        }

        /* SUBMIT BUTTON */
        .btn-login-submit {
          width: 100%;
          padding: 14px 24px;
          font-size: 15px;
          font-weight: 600;
          color: #ffffff;
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          border: none;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px -3px rgba(37, 99, 235, 0.4);
          margin-top: 4px;
          font-family: inherit;
        }

        .btn-login-submit:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
          box-shadow: 0 6px 20px -3px rgba(37, 99, 235, 0.5);
          transform: translateY(-1px);
        }

        .btn-login-submit:active:not(:disabled) {
          transform: translateY(0);
        }

        .btn-login-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-login-loading {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .login-spinner-sm {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: loginSpin 0.8s linear infinite;
        }

        @keyframes loginSpin {
          to { transform: rotate(360deg); }
        }

        /* DIVIDER */
        .login-divider {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 28px;
        }

        .login-divider::before,
        .login-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .login-divider span {
          font-size: 12px;
          color: #94a3b8;
          white-space: nowrap;
        }

        /* SUPPORT */
        .login-support {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 24px;
          font-size: 13px;
          color: #94a3b8;
        }

        .login-support a {
          color: #2563eb;
          text-decoration: none;
          font-weight: 500;
        }

        .login-support a:hover {
          text-decoration: underline;
        }

        .login-form-footer {
          padding: 24px 40px;
          text-align: center;
        }

        .login-form-footer p {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
        }

        /* FORGOT PASSWORD */
        .forgot-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .forgot-description {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
          margin: 0;
        }

        .forgot-success,
        .forgot-error {
          text-align: center;
          padding: 20px 0;
        }

        .forgot-success h3 {
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 12px 0;
        }

        .forgot-success p,
        .forgot-error p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
          margin: 0;
        }

        .btn-forgot-back {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          padding: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 12px;
          font-family: inherit;
        }

        .btn-forgot-back:hover {
          background: #f8fafc;
          color: #374151;
        }

        /* RESPONSIVE */
        @media (max-width: 1024px) {
          .login-brand-panel {
            width: 40%;
          }
          .brand-content {
            padding: 40px 32px;
          }
        }

        @media (max-width: 768px) {
          .login-page {
            flex-direction: column;
          }
          .login-brand-panel {
            display: none;
          }
          .login-form-panel {
            padding: 24px 20px;
          }
          .mobile-logo {
            display: flex;
          }
          .form-header h2 {
            font-size: 24px;
          }
          .login-form-footer {
            padding: 16px 20px;
          }
        }

        @media (max-width: 480px) {
          .login-form-panel {
            padding: 20px 16px;
            justify-content: flex-start;
            padding-top: 40px;
          }
          .form-header h2 {
            font-size: 22px;
          }
          .btn-login-submit {
            padding: 12px 20px;
            font-size: 14px;
          }
        }
      `}</style>

      <div className="login-page">
        {/* Left Panel - Branding */}
        <div className="login-brand-panel">
          <div className="brand-content">
            <div className="brand-circle brand-circle-1"></div>
            <div className="brand-circle brand-circle-2"></div>
            <div className="brand-circle brand-circle-3"></div>

            <div className="brand-logo">
              <a
                href="https://flexboost.co.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="logo-link"
                title="Visit FlexBoost Official Website"
              >
                <div className="logo-image-wrapper">
                  <img
                    src={COMPANY_LOGO}
                    alt="FlexBoost International Ltd"
                  />
                </div>
              </a>
              <h1 className="brand-name">FlexBoost</h1>
              <span className="brand-tagline">Your Growth Matters</span>
            </div>

            <div className="brand-features">
              <div className="brand-feature">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <div>
                  <h3>Secure Platform</h3>
                  <p>Enterprise-grade security with encrypted data transmission</p>
                </div>
              </div>
              <div className="brand-feature">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                </div>
                <div>
                  <h3>Fast Processing</h3>
                  <p>Streamlined loan approval and disbursement workflow</p>
                </div>
              </div>
              <div className="brand-feature">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </div>
                <div>
                  <h3>Real-time Analytics</h3>
                  <p>Comprehensive dashboards with live portfolio insights</p>
                </div>
              </div>
            </div>

            <div className="brand-footer">
              <p>"Empowering lenders with intelligent loan management solutions."</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="login-form-panel">
          <div className="form-container">
            {/* Mobile Logo */}
            <div className="mobile-logo">
              <a
                href="https://flexboost.co.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-logo-link"
                title="Visit FlexBoost Official Website"
              >
                <img
                  src={COMPANY_LOGO}
                  alt="FlexBoost International Ltd"
                  className="mobile-logo-img"
                />
              </a>
              <span>FlexBoost</span>
            </div>

            <div className="form-header">
              <h2>Welcome back</h2>
              <p>Enter your credentials to access your account</p>
            </div>

            {error && (
              <div className="login-alert login-alert-error">
                <div className="login-alert-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                </div>
                <span style={{ flex: 1 }}>{error}</span>
                <button className="login-alert-close" onClick={() => setError('')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="login-form-group">
                <label htmlFor="login-email">Email Address</label>
                <div className="login-input-wrapper">
                  <div className="login-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <input
                    id="login-email"
                    type="email"
                    className="login-input"
                    placeholder="name@company.com"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-form-group">
                <div className="form-label-row">
                  <label htmlFor="login-password">Password</label>
                  <button
                    type="button"
                    className="login-text-link"
                    onClick={() => setShowForgot(true)}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="login-input-wrapper">
                  <div className="login-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    className="login-input"
                    placeholder="Enter your password"
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-options">
                <label className="login-checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={form.rememberMe}
                    onChange={(e) => setForm({ ...form, rememberMe: e.target.checked })}
                  />
                  <span className="login-checkmark"></span>
                  <span className="login-checkbox-label">Remember me for 30 days</span>
                </label>
              </div>

              <button type="submit" className="btn-login-submit" disabled={loading}>
                {loading ? (
                  <span className="btn-login-loading">
                    <span className="login-spinner-sm"></span>
                    Signing in...
                  </span>
                ) : (
                  <>
                    Sign In
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="login-divider">
              <span>Secured with 256-bit encryption</span>
            </div>

            <div className="login-support">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>Need help? Contact <a href="mailto:support@flexboost.com">support@flexboost.com</a></span>
            </div>
          </div>

          <div className="login-form-footer">
            <p>© {new Date().getFullYear()} FlexBoost International Ltd. All rights reserved.</p>
          </div>
        </div>

        {/* Forgot Password Modal */}
        <Modal isOpen={showForgot} onClose={() => { setShowForgot(false); setForgotMsg(''); setForgotEmail(''); }} title="Reset Password">
          {forgotMsg === 'success' ? (
            <div className="forgot-success">
              <div style={{ marginBottom: '20px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3>Check your email</h3>
              <p>We've sent a password reset link to <strong>{forgotEmail}</strong>. The link will expire in 15 minutes.</p>
              <button className="btn-login-submit" style={{ marginTop: '24px' }} onClick={() => { setShowForgot(false); setForgotMsg(''); setForgotEmail(''); }}>
                Back to Sign In
              </button>
              <button className="login-text-link" style={{ marginTop: '12px', display: 'block', textAlign: 'center' }} onClick={() => setForgotMsg('')}>
                Didn't receive the email? Try again
              </button>
            </div>
          ) : forgotMsg ? (
            <div className="forgot-error">
              <div style={{ marginBottom: '20px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <p style={{ color: '#dc2626', textAlign: 'center' }}>{forgotMsg}</p>
              <button className="btn-login-submit" style={{ marginTop: '20px', background: '#64748b', boxShadow: 'none' }} onClick={() => setForgotMsg('')}>
                Try Again
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="forgot-form">
              <p className="forgot-description">
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </p>
              <div className="login-form-group">
                <label>Email Address</label>
                <div className="login-input-wrapper">
                  <div className="login-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <input
                    type="email"
                    className="login-input"
                    placeholder="name@company.com"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <button type="submit" className="btn-login-submit" disabled={forgotLoading}>
                {forgotLoading ? (
                  <span className="btn-login-loading">
                    <span className="login-spinner-sm"></span>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </button>
              <button type="button" className="btn-forgot-back" onClick={() => setShowForgot(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"/>
                  <polyline points="12 19 5 12 12 5"/>
                </svg>
                Back to Sign In
              </button>
            </form>
          )}
        </Modal>
      </div>
    </>
  );
}